import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { ArticleCard } from "../components/ArticleCard";
import { CATEGORY_LABELS, SUBCATEGORY_LABELS, SUBCATEGORIES } from "../lib/constants";
import { Link } from "react-router-dom";

export default function CategoryPage() {
    const { category, subcategory } = useParams();
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    const label = subcategory
        ? SUBCATEGORY_LABELS[subcategory]
        : CATEGORY_LABELS[category] || category;

    const fetchPage = useCallback(async (p) => {
        const params = { category, page: p, page_size: 12 };
        if (subcategory) params.subcategory = subcategory;
        const { data } = await api.get("/articles", { params });
        return data;
    }, [category, subcategory]);

    useEffect(() => {
        setLoading(true);
        setPage(1);
        fetchPage(1).then((data) => {
            setItems(data.items);
            setHasMore(data.has_more);
        }).catch(() => setItems([])).finally(() => setLoading(false));
    }, [category, subcategory, fetchPage]);

    const loadMore = async () => {
        setLoadingMore(true);
        const next = page + 1;
        const data = await fetchPage(next);
        setItems((prev) => [...prev, ...data.items]);
        setPage(next);
        setHasMore(data.has_more);
        setLoadingMore(false);
    };

    const chip = (active) =>
        `px-3.5 py-1.5 rounded-full text-[13px] font-medium transition-colors duration-150 ${
            active
                ? "border-2 border-foreground bg-accent text-accent-foreground shadow-inset"
                : "border-2 border-foreground text-dim hover:text-foreground hover:border-line-strong"
        }`;

    return (
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="category-page">
            <div className="pb-7 mb-8 border-b-[3px] border-foreground">
                <span className="eyebrow inline-block border-2 border-foreground bg-accent px-2 py-1 text-accent-foreground">
                    {subcategory ? "Personal Finance" : "Section"}
                </span>
                <h1
                    className="font-display uppercase text-3xl sm:text-5xl tracking-[-0.02em] mt-2.5"
                    data-testid="category-title"
                >
                    {label}
                </h1>
            </div>

            {category === "personal-finance" && (
                <div className="flex flex-wrap gap-2 mb-10">
                    <Link to="/category/personal-finance" className={chip(!subcategory)}>All</Link>
                    {SUBCATEGORIES.map((s) => (
                        <Link
                            key={s}
                            to={`/category/personal-finance/${s}`}
                            data-testid={`subcat-chip-${s}`}
                            className={chip(subcategory === s)}
                        >
                            {SUBCATEGORY_LABELS[s]}
                        </Link>
                    ))}
                </div>
            )}

            {loading ? (
                <>
                <p className="flex items-center gap-2 font-mono text-xs text-dim mb-6">
                    <span className="w-2 h-2 bg-foreground animate-pulse-dot" />
                    Loading stories…
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="rounded-2xl border-2 border-foreground overflow-hidden">
                            <div className="shimmer aspect-[3/2]" />
                            <div className="p-5 space-y-3">
                                <div className="shimmer h-3 w-16 rounded" />
                                <div className="shimmer h-4 w-full rounded" />
                                <div className="shimmer h-4 w-2/3 rounded" />
                            </div>
                        </div>
                    ))}
                </div>
                </>
            ) : items.length === 0 ? (
                <div className="panel p-14 text-center" data-testid="category-empty">
                    <p className="font-mono text-sm text-dim">No stories in this section yet.</p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {items.map((a) => (
                            <ArticleCard key={a.id} article={a} />
                        ))}
                    </div>
                    {hasMore && (
                        <div className="mt-14 text-center">
                            <button
                                onClick={loadMore}
                                disabled={loadingMore}
                                data-testid="load-more-btn"
                                className="px-9 py-3 border-[3px] border-foreground bg-surface text-sm font-bold uppercase tracking-wider text-foreground shadow-card hover:bg-accent hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-inset transition-[transform,box-shadow,background-color] duration-150 disabled:opacity-50"
                            >
                                {loadingMore ? "Loading…" : "Load More"}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
